<template>
  <SelectResponse
    ref="select"
    v-bind="$props"
    :placeholder="defaultPlaceholder"
    :isLoading="isLoading"
    :data="dataSource.records"
    :filter-method="remoteMethod"
    v-on="$listeners"
    @on-input="handlerInput"
    @on-focus="handlerFocus"
  >
    <template v-slot:prefix>
      <slot name="prefix"></slot>
    </template>
    <template v-slot:select-info>
      <div class="select-info-wrap">
        <div class="select-info" :title="L10n('select-info')">
          {{L10n('select-info')}}
        </div>
      </div>
      <div class="select-info-wrap__loading" v-loading="isLoading"></div>
    </template>
  </SelectResponse>
</template>

<script>
import SelectResponse from '../SelectResponse/Index'
import { api } from '@/services/api'
import axios from 'axios'
export default {
  name: 'SelectResponseAutoComplete',
  components: {
    SelectResponse
  },
  props: {
    model: { required: true },
    fields: { type: Object, default: () => ({ title: 'title', id: 'id' }) },
    placeholder: { type: String },
    clearable: { type: Boolean, default: true },
    filterable: { type: Boolean, default: true },
    multiple: { type: Boolean, default: false },
    multipleLimit: { type: Number, default: -1 },
    disabled: { type: Boolean, default: false },
    size: { type: String, default: '' },
    columns: { type: Array, default: () => ([]) },
    getLabel: { type: Function },

    api: { type: String, default: '', required: true },
    filters: { type: String, default: '' },
    searchAllColumns: { type: Boolean, default: false },
    // eslint-disable-next-line
    templateFilter: { type: [String, Function], default: '${data.fields.title}.contains("${data.search}")' },
    // eslint-disable-next-line
    initTemplateFilter: { type: String, default: '${data.fields.id}==${data.value}' },
    selected: { type: String, default: '' },
    take: { type: Number, default: 30 },
    orderBy: { type: Object, default: () => ({ fields: undefined, order: 'asc' }) }
  },
  watch: {
    model: {
      immediate: true,
      handler (value) {
        this.$nextTick()
          .then(() => {
            if (value && !this.hasValueDataSource) {
              this.updateDataSource()
            }
          })
      }
    }
  },
  data () {
    return {
      // status
      isLoading: false,
      // value
      search: '',
      // result data
      dataSource: { records: [], map: {}, count: 0 },
      // debounce
      requestGetDataDebounce: this.$_.debounce(this.requestGetData, 500),
      requestCancel: function () {}
    }
  },
  computed: {
    defaultPlaceholder () {
      return this.placeholder || this.L10n('select')
    },
    paramsSelect () {
      let selected = this.selected ? this.selected.split(',') : [this.fields.id, this.fields.title]
      return selected.join(',')
    },
    paramsOrder () {
      let { fields = this.fields.title, order = 'asc' } = this.orderBy
      return `${fields} ${order}`
    },
    hasValueDataSource () {
      return !!(this.model && this.dataSource.map[this.model])
    },
    modelItem () {
      return this.dataSource.map[this.model] || null
    }
  },
  methods: {
    // TODO USER INTERFACE
    updateDataSource () {
      return Promise.resolve()
        .then(() => {
          this.requestCancel()
          this.requestGetData()
        })
    },
    focus () {
      this.$refs['select'].focus()
    },
    // TODO END USER INTERFACE
    // Search Mobile
    handlerInput (value) {
      this.requestGetDataDebounce(value)
    },
    // Search desktop
    remoteMethod (value) {
      this.requestGetDataDebounce(value)
    },
    handlerFocus () {
      if (!this.hasValueDataSource) {
        this.updateDataSource()
      }
    },
    requestGetData (search) {
      const CancelToken = axios.CancelToken
      let params = this.createParams(search)
      // reset
      this.isLoading = true
      // load data
      return api
        .get(`${this.api}`, {
          params: params,
          cancelToken: new CancelToken((c) => {
            this.requestCancel = c
          })
        })
        .then(response => {
          this.dataSource = response.data.value.reduce((result, value, index) => {
            result.records.push(result.map[value[this.fields.id]] = { ...value })
            return result
          }, { records: [], map: {}, count: response.data.count })
          return this.dataSource
        })
        .catch(error => {
          this.dataSource = { records: [], map: {}, count: 0 }
          this.requestErrorHandler(error, this.L10n('general-error'))
        })
        .finally(() => {
          this.requestCancel = function () {}
          this.isLoading = false
        })
    },
    createParams (search) {
      let params = {
        'select': this.paramsSelect,
        'filter': this.createParamsFilter(search),
        'count': true,
        'take': this.take,
        'orderBy': this.paramsOrder
      }
      return JSON.parse(JSON.stringify(params))
    },
    createParamsFilter (search) {
      let filters = []
      // static filters
      if (this.filters) {
        filters.push(`( ${this.filters} )`)
      }
      let data = { fields: this.fields, value: this.model, search }
      let template = ''
      if (search && this.searchAllColumns && this.columns) {
        template = this.createTemplateFilterAllColumns(data)
      } else if (search && !this.searchAllColumns) {
        template = this.templateFilter
      } else if (this.model) {
        template = this.initTemplateFilter
      }

      let templateFilterValue = this.checkEs6AndRun(template, data)
      if (templateFilterValue) {
        filters.push(templateFilterValue)
      }
      return filters.join(' && ') || undefined
    },
    createTemplateFilterAllColumns (data) {
      let result = []
      this.columns.forEach((column) => {
        let template = ''
        switch (column.type) {
          case 'boolean': {
            template = templateBooleanColumn(column.originField, data)
            break
          }
          case 'date':
            template = templateDateColumn(column.originField, data)
            break
          case 'string':
            template = templateStringColumn(column.originField, data)
            break
          case 'number':
            template = templateNumberColumn(column.originField, data)
            break
          case 'custom':
            template = column.customFilter(column, data)
            break
          default:
            console.error(`Incorrect type ${column.type} for column ${column.title}`)
        }
        if (column.originField && column.type && template) {
          result.push(template)
        }
      })
      return result.length ? `( ${result.join(' || ')} )` : ''

      function templateBooleanColumn (originField, data) {
        let isTrue = /true/g.test(data.search)
        let isFalse = /false/g.test(data.search)
        let value = isTrue
          ? true
          : isFalse
            ? false
            : ''
        return (isTrue || isFalse) ? `${originField} == ${value}` : ''
      }

      function templateDateColumn (originField, data) {
        return data.search ? `Convert.ToString(${originField}).contains("${data.search}")` : ''
      }

      function templateStringColumn (originField, data) {
        return data.search ? `${originField}.contains("${data.search}")` : ''
      }

      function templateNumberColumn (originField, data) {
        let searchNumber = parseInt(data.search)
        return isNaN(searchNumber) ? '' : `${originField} == ${searchNumber}`
      }
    }
  }
}
</script>
<style lang="stylus">
  .el-select-dropdown.mode-column{
    .el-select-dropdown__wrap{
      max-height: 75vh;
    }
  }
  .select-info-wrap{
    .mode-column{
      margin-top 0
    }
    margin-top -6px
    margin-bottom 4rem
    min-width 15rem
    background white
    &__loading{
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
    .select-info{
      padding 0 1rem
      z-index 1
      left 0
      right 0
      height 4rem
      line-height 4rem
      text-align center
      position absolute
      font-size 1.3rem
      background #f2fbfe
      color #65646c
      white-space nowrap
      overflow hidden
      text-overflow ellipsis
    }
  }
</style>
